import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

import {
  DiscoveryResult,
  Explanation,
  InstrumentPage,
  Job,
  NewsResult,
  NewsCuration,
  NewsTag,
  TagPage,
  RecentRun,
  ReviewZone,
  ZonePage,
  EvidenceMode,
  NewsSource,
  SourceCapabilities,
} from './types';

const BASE = '/scenario-builder/admin/market-discovery';

export class RequestError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export const errorMessage = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message;
  if (Array.isArray(value))
    return value.map(errorMessage).filter(Boolean).join(' ');
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['message', 'detail', 'refusal', 'error']) {
      if (record[key]) return errorMessage(record[key]);
    }
    return Object.values(record).map(errorMessage).filter(Boolean).join(' ');
  }
  return '';
};

const request = async <Result>(
  path: string,
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  data: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<Result> => {
  const response = await apiCaller({
    path,
    method,
    data,
    signal,
    apiUrl: API_URL,
  });
  if (response.status < 200 || response.status >= 300) {
    throw new RequestError(
      errorMessage(response.result) ||
        'The request could not be completed. Please try again.',
      response.status,
    );
  }
  return response.result as Result;
};

export const fetchNewsCuration = (id: number | string, signal?: AbortSignal) =>
  request<NewsCuration>(
    `/news/${encodeURIComponent(id)}/curation`,
    'GET',
    {},
    signal,
  );

export const updateNewsCuration = (
  id: number | string,
  data: {
    expected_revision: string;
    headline?: string;
    body?: string;
    add_tag_ids?: number[];
    remove_tag_ids?: number[];
  },
) =>
  request<NewsCuration>(
    `/news/${encodeURIComponent(id)}/curation`,
    'PATCH',
    data,
  );

export const findTags = (q: string, page = 1, signal?: AbortSignal) =>
  request<TagPage>('/tags/search', 'GET', { q, page, page_size: 40 }, signal);

export const resolveTag = (text: string, instrumentId?: number | null) =>
  request<NewsTag>('/tags/resolve', 'POST', {
    text,
    ...(instrumentId != null ? { instrument_id: instrumentId } : {}),
  });

/** `vetted` keeps only the asset catalogue's tier members (synthetic bond issues excluded). */
export const findInstruments = (
  q: string,
  page = 1,
  pageSize = 100,
  signal?: AbortSignal,
  vetted = false,
) =>
  request<InstrumentPage>(
    `${BASE}/instruments`,
    'GET',
    { q, page, page_size: pageSize, ...(vetted ? { vetted: 1 } : {}) },
    signal,
  );

export const startDiscovery = (
  data: { instrument_id: number; start_date?: string; end_date?: string },
  signal?: AbortSignal,
) => request<Job<DiscoveryResult>>(BASE, 'POST', data, signal);

export const fetchRecentRuns = (signal?: AbortSignal, instrumentId?: number) =>
  request<{ results: RecentRun[] }>(
    BASE,
    'GET',
    { page_size: 20, ...(instrumentId ? { instrument_id: instrumentId } : {}) },
    signal,
  );
export const fetchCapabilities = (signal?: AbortSignal) =>
  request<SourceCapabilities>(`${BASE}/capabilities`, 'GET', {}, signal);
export const startCorpus = (
  runId: string,
  periodId: string,
  window: EvidenceMode,
  signal?: AbortSignal,
) =>
  request<Job<NewsResult>>(
    `${BASE}/${encodeURIComponent(runId)}/corpus`,
    'POST',
    { period_id: periodId, window },
    signal,
  );
const ZONES = '/scenario-builder/admin/market-review-zones';
export const fetchReviewZones = (
  instrumentId: number,
  signal?: AbortSignal,
  page = 1,
) =>
  request<ZonePage>(
    ZONES,
    'GET',
    { instrument_id: instrumentId, page_size: 100, page },
    signal,
  );
export const fetchReviewZone = (id: string) =>
  request<ReviewZone>(`${ZONES}/${encodeURIComponent(id)}`, 'GET', {});
export const saveReviewZone = (data: Record<string, unknown>, id?: string) =>
  request<ReviewZone>(
    id ? `${ZONES}/${encodeURIComponent(id)}` : ZONES,
    id ? 'PATCH' : 'POST',
    data,
  );
export const archiveReviewZone = (id: string, revision: number) =>
  request<unknown>(`${ZONES}/${encodeURIComponent(id)}`, 'DELETE', {
    expected_revision: revision,
  });

export const fetchJob = <Result>(id: string, signal?: AbortSignal) =>
  request<Job<Result>>(`${BASE}/${encodeURIComponent(id)}`, 'GET', {}, signal);

export const fetchNews = (
  runId: string,
  periodId: string,
  signal?: AbortSignal,
  window: EvidenceMode = 'period',
) =>
  request<NewsResult>(
    `${BASE}/${encodeURIComponent(runId)}/news`,
    'GET',
    { period_id: periodId, window },
    signal,
  );

export const startExplanation = (
  runId: string,
  periodId: string,
  signal?: AbortSignal,
  options?: {
    window: EvidenceMode;
    source: NewsSource;
    evidence_job_id?: string;
  },
) =>
  request<Job<Explanation>>(
    `${BASE}/${encodeURIComponent(runId)}/explain`,
    'POST',
    { period_id: periodId, ...options },
    signal,
  );

const pause = (signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const cancel = () => {
      window.clearTimeout(timer);
      reject(new DOMException('Request cancelled', 'AbortError'));
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', cancel);
      resolve();
    }, 1500);
    if (signal.aborted) cancel();
    else signal.addEventListener('abort', cancel, { once: true });
  });

/** Stop polling immediately when the view or its current request changes. */
export const waitForJob = async <Result>(
  initial: Job<Result>,
  signal: AbortSignal,
): Promise<Job<Result> & { result: Result }> => {
  let job = initial;
  while (!signal.aborted) {
    const state = job.status.toLowerCase();
    if (
      ['succeeded', 'completed', 'complete', 'done', 'success'].includes(state)
    ) {
      if (!job.result)
        throw new Error(
          'The analysis finished without a result. Please try again.',
        );
      return job as Job<Result> & { result: Result };
    }
    if (
      [
        'failed',
        'cancelled',
        'canceled',
        'refused',
        'blocked',
        'lost',
      ].includes(state) ||
      Boolean(errorMessage(job.refusal))
    ) {
      throw new Error(
        errorMessage(job.refusal) ||
          errorMessage(job.error) ||
          'This analysis could not be completed. Please try again.',
      );
    }
    await pause(signal);
    job = await fetchJob<Result>(job.id, signal);
  }
  throw new DOMException('Request cancelled', 'AbortError');
};
